import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import {
  Users, Package, TrendingUp, CheckCircle, Clock, Truck,
  XCircle, DollarSign, MessageCircle, FileText, AlertCircle, QrCode, LogOut, Handshake
} from 'lucide-react';
import { Button } from '../ui/button';
import { SignupQrCode } from './SignupQrCode';
import { AdminOrders } from './AdminOrders';
import { AdminPartnerships } from './AdminPartnerships';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

export const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchStats();
    } else if (activeTab === 'verifications') {
      fetchVerifications();
    } else if (activeTab === 'revenue') {
      fetchRevenue();
    } else if (activeTab === 'support') {
      fetchTickets();
    } else if (activeTab === 'leads') {
      fetchLeads();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/verifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifications(response.data.verifications);
    } catch (error) {
      console.error('Error fetching verifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async (status = null) => {
    try {
      setLoading(true);
      const url = status ? `${API}/admin/support?status=${status}` : `${API}/admin/support`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationAction = async (userId, action, reason = '') => {
    try {
      await axios.put(`${API}/admin/verifications/${userId}`, {
        status: action,
        rejection_reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Verificação ${action === 'approved' ? 'aprovada' : 'rejeitada'} com sucesso!`);
      fetchVerifications();
      setSelectedVerification(null);
    } catch (error) {
      console.error('Error updating verification:', error);
      alert('Erro ao atualizar verificação');
    }
  };

  const handleTicketStatusChange = async (ticketId, status) => {
    try {
      await axios.put(`${API}/admin/support/${ticketId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Estado atualizado com sucesso!');
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Erro ao atualizar ticket');
    }
  };

  const handleLeadContact = async (leadId, contacted) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, {
        contacted: contacted
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Erro ao atualizar status do lead');
    }
  };

  // Dashboard Tab
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Package className="w-8 h-8" />}
          title="Total Pedidos"
          value={stats?.total_orders || 0}
          color="blue"
        />
        <StatCard
          icon={<Users className="w-8 h-8" />}
          title="Clientes"
          value={stats?.total_customers || 0}
          color="green"
        />
        <StatCard
          icon={<Truck className="w-8 h-8" />}
          title="Estafetas"
          value={stats?.total_drivers || 0}
          color="purple"
        />
        <StatCard
          icon={<DollarSign className="w-8 h-8" />}
          title="Receita Total"
          value={`€${stats?.total_revenue || 0}`}
          color="yellow"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by Status */}
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Pedidos por Estado</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats?.orders_by_status ? Object.entries(stats.orders_by_status).map(([key, value]) => ({
                  name: getStatusLabel(key),
                  value: value
                })) : []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {stats?.orders_by_status && Object.keys(stats.orders_by_status).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Day */}
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Pedidos (Últimos 7 Dias)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.orders_by_day || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts */}
      {stats?.pending_verifications > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>{stats.pending_verifications}</strong> verificações pendentes aguardam revisão
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Verifications Tab
  const renderVerifications = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Verificações Pendentes</h2>
        <span className="text-sm text-gray-600">{verifications.length} pendentes</span>
      </div>

      {verifications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nenhuma verificação pendente
        </div>
      ) : (
        <div className="grid gap-4">
          {verifications.map((user) => {
            const verification = user.user_type === 'customer' 
              ? user.customer_verification 
              : user.driver_verification;
            
            return (
              <div key={user.id} className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{user.name}</h3>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${
                      user.user_type === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {user.user_type === 'customer' ? 'Cliente' : 'Estafeta'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setSelectedVerification(user)}
                      className="bg-blue-500 text-white"
                    >
                      Ver Detalhes
                    </Button>
                  </div>
                </div>

                {selectedVerification?.id === user.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div>
                      <strong>Tipo de Documento:</strong> {verification.id_document_type?.toUpperCase() || 'N/A'}
                    </div>
                    {user.user_type === 'driver' && (
                      <>
                        <div>
                          <strong>Tipo de Veículo:</strong> {verification.vehicle_type || 'N/A'}
                        </div>
                        <div>
                          <strong>Matrícula:</strong> {verification.vehicle_plate || 'N/A'}
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleVerificationAction(user.id, 'approved')}
                        className="bg-green-500 text-white flex-1"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprovar
                      </Button>
                      <Button
                        onClick={() => {
                          const reason = prompt('Motivo da rejeição:');
                          if (reason) handleVerificationAction(user.id, 'rejected', reason);
                        }}
                        className="bg-red-500 text-white flex-1"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Rejeitar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Orders Tab
  // Revenue Tab
  const renderRevenue = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Faturação</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={<DollarSign className="w-8 h-8" />}
          title="Receita Total"
          value={`€${revenue?.total_revenue || 0}`}
          color="green"
        />
        <StatCard
          icon={<Package className="w-8 h-8" />}
          title="Pedidos Entregues"
          value={revenue?.total_orders || 0}
          color="blue"
        />
        <StatCard
          icon={<TrendingUp className="w-8 h-8" />}
          title="Valor Médio"
          value={`€${revenue?.average_order_value || 0}`}
          color="purple"
        />
      </div>

      {/* Revenue Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Receita por Mês</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenue?.revenue_by_month || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} name="Receita (€)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Orders Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Pedidos Recentes</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estafeta</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {revenue?.recent_orders?.slice(0, 10).map((order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {new Date(order.created_at).toLocaleDateString('pt-PT')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{order.customer_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{order.driver_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">€{order.final_price?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Support Tab
  const renderSupport = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Suporte</h2>
        <div className="flex gap-2">
          <Button onClick={() => fetchTickets()} variant="outline">Todos</Button>
          <Button onClick={() => fetchTickets('open')} variant="outline">Abertos</Button>
          <Button onClick={() => fetchTickets('in_progress')} variant="outline">Em Progresso</Button>
          <Button onClick={() => fetchTickets('resolved')} variant="outline">Resolvidos</Button>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
        <div className="flex">
          <MessageCircle className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Emails de suporte enviados para: <strong>duvidastuggo@gmail.com</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {tickets.map((ticket) => (
          <div key={ticket.id} className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{ticket.subject}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    ticket.priority === 'high' ? 'bg-red-100 text-red-800' :
                    ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {ticket.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{ticket.user_name} - {ticket.user_email}</p>
                <p className="text-sm text-gray-700">{ticket.message}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Criado: {new Date(ticket.created_at).toLocaleString('pt-PT')}
                </p>
              </div>
              <select
                value={ticket.status}
                onChange={(e) => handleTicketStatusChange(ticket.id, e.target.value)}
                className="ml-4 px-3 py-2 border rounded-lg text-sm"
              >
                <option value="open">Aberto</option>
                <option value="in_progress">Em Progresso</option>
                <option value="resolved">Resolvido</option>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Leads Tab
  const renderLeads = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Leads Capturados</h2>
        <span className="text-sm text-gray-600">{leads.length} leads totais</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">
            {leads.filter(l => l.lead_type === 'customer').length}
          </div>
          <div className="text-sm text-gray-600">Clientes Interessados</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {leads.filter(l => l.lead_type === 'driver').length}
          </div>
          <div className="text-sm text-gray-600">Estafetas Interessados</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {leads.filter(l => !l.contacted).length}
          </div>
          <div className="text-sm text-gray-600">Não Contactados</div>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nenhum lead capturado ainda
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalhes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lead.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">{lead.email}</a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">{lead.phone}</a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      lead.lead_type === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {lead.lead_type === 'customer' ? 'Cliente' : 'Estafeta'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {lead.lead_type === 'customer' && lead.business ? `Negócio: ${lead.business}` : ''}
                    {lead.lead_type === 'driver' && lead.vehicle ? `Veículo: ${lead.vehicle}` : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(lead.created_at).toLocaleDateString('pt-PT')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleLeadContact(lead.id, !lead.contacted)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        lead.contacted 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {lead.contacted ? '✓ Contactado' : 'Marcar Contactado'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading && !stats && !verifications.length) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-between items-center py-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Painel de Administração</h1>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600" data-testid="admin-user-label">
                <Users className="w-4 h-4 inline mr-1" />
                Administrador
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="rounded-full"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Terminar sessão
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto whitespace-nowrap" data-testid="admin-tabs-nav">
            <TabButton
              active={activeTab === 'dashboard'}
              onClick={() => setActiveTab('dashboard')}
              icon={<TrendingUp className="w-5 h-5" />}
              label="Dashboard"
            />
            <TabButton
              active={activeTab === 'verifications'}
              onClick={() => setActiveTab('verifications')}
              icon={<FileText className="w-5 h-5" />}
              label="Verificações"
              badge={stats?.pending_verifications}
            />
            <TabButton
              active={activeTab === 'orders'}
              onClick={() => setActiveTab('orders')}
              icon={<Package className="w-5 h-5" />}
              label="Pedidos"
              testId="admin-tab-orders"
            />
            <TabButton
              active={activeTab === 'revenue'}
              onClick={() => setActiveTab('revenue')}
              icon={<DollarSign className="w-5 h-5" />}
              label="Faturação"
            />
            <TabButton
              active={activeTab === 'support'}
              onClick={() => setActiveTab('support')}
              icon={<MessageCircle className="w-5 h-5" />}
              label="Suporte"
            />
            <TabButton
              active={activeTab === 'leads'}
              onClick={() => setActiveTab('leads')}
              icon={<Users className="w-5 h-5" />}
              label="Leads"
              badge={leads.filter(l => !l.contacted).length || null}
            />
            <TabButton
              active={activeTab === 'partnerships'}
              onClick={() => setActiveTab('partnerships')}
              icon={<Handshake className="w-5 h-5" />}
              label="Parcerias"
              testId="admin-tab-partnerships"
            />
            <TabButton
              active={activeTab === 'qrcode'}
              onClick={() => setActiveTab('qrcode')}
              icon={<QrCode className="w-5 h-5" />}
              label="QR Code"
              testId="admin-tab-qrcode"
            />
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'verifications' && renderVerifications()}
        {activeTab === 'orders' && <AdminOrders />}
        {activeTab === 'revenue' && renderRevenue()}
        {activeTab === 'support' && renderSupport()}
        {activeTab === 'leads' && renderLeads()}
        {activeTab === 'partnerships' && <AdminPartnerships />}
        {activeTab === 'qrcode' && <SignupQrCode />}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <div className="flex items-center">
        <div className={`${colorClasses[color]} text-white p-3 rounded-lg`}>
          {icon}
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label, badge, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex flex-shrink-0 items-center py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-tuggo-mint text-tuggo-mint'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {icon}
    <span className="ml-2">{label}</span>
    {badge > 0 && (
      <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
        {badge}
      </span>
    )}
  </button>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'Aguardando', color: 'bg-yellow-100 text-yellow-800' },
    accepted: { label: 'Aceito', color: 'bg-blue-100 text-blue-800' },
    picked_up: { label: 'Em Curso', color: 'bg-purple-100 text-purple-800' },
    delivered: { label: 'Entregue', color: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-2 py-1 text-xs rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

const getStatusLabel = (status) => {
  const labels = {
    pending: 'Aguardando',
    accepted: 'Aceito',
    picked_up: 'Em Curso',
    delivered: 'Entregue',
    cancelled: 'Cancelado'
  };
  return labels[status] || status;
};
